import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmt2 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function CartPage() {
  const [cart, setCart] = useState({ items: [], subtotal_final: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/cart"); setCart(r.data); } catch (e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { (async () => { await load(); })(); }, []);

  const remove = async (id) => {
    try { await api.delete(`/cart/items/${id}`); await load(); toast.success("Removed"); } catch (e) { toast.error("Could not remove"); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-10" data-testid="cart-page">
      <h1 className="font-display text-3xl font-semibold tracking-tight">Your cart</h1>
      {loading ? (
        <p className="text-muted-foreground mt-6">Loading…</p>
      ) : cart.items.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-dashed border-border p-10 text-center" data-testid="cart-empty">
          <p className="font-medium">Your cart is empty</p>
          <p className="text-sm text-muted-foreground mt-1">Browse experiences to add your first trip.</p>
          <Link to="/"><Button className="mt-4 rounded-xl">Explore experiences</Button></Link>
        </div>
      ) : (
        <div className="mt-6 grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 grid gap-3">
            {cart.items.map((it) => (
              <div key={it.cart_item_id} className="rounded-2xl bg-card border border-border p-4 flex gap-4" data-testid="cart-item">
                <img src={it.listing.image} alt={it.listing.title} className="h-24 w-32 object-cover rounded-xl" />
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold">{it.listing.title}</h3>
                      <p className="text-sm text-muted-foreground">{it.listing.location}</p>
                      <p className="text-sm mt-1">Date: <span className="font-medium">{it.date}</span> · Guests: <span className="font-medium">{it.guests}</span></p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => remove(it.cart_item_id)} className="rounded-xl" data-testid="cart-remove-item-button"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <div className="mt-2 text-sm">
                    <span className="text-muted-foreground">Total: </span>
                    <span className="font-semibold" data-testid="cart-line-final">{fmt2(it.line_final)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <aside className="lg:col-span-1">
            <div className="rounded-2xl bg-card border border-border p-5 lg:sticky lg:top-24">
              <h2 className="font-semibold mb-3">Order summary</h2>
              <div className="rounded-xl bg-muted border border-border p-3 flex items-center justify-between" data-testid="cart-summary-total">
                <span className="text-sm text-muted-foreground">Total</span>
                <span className="font-semibold" data-testid="cart-price-breakdown-total">{fmt2(cart.subtotal_final)}</span>
              </div>
              <Button className="mt-4 w-full rounded-xl h-11" onClick={() => navigate("/checkout")} data-testid="cart-go-to-checkout-button">Proceed to checkout</Button>
              <p className="mt-3 text-xs text-muted-foreground">All-inclusive pricing. No hidden charges.</p>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
